/* eslint-disable no-nested-ternary */
import { FC, useEffect, useState } from 'react'
import { Panel, Col, ButtonToolbar } from 'rsuite'
import {
  Button,
  Toolbar,
  CardTable,
  Modal,
  TextInput,
  Row,
  Section,
  FormikErrorMessage
} from 'shared'
import { FbAuthProvider } from 'hooks/facebook'
import './channelOnboardingDetails.scss'
import '../../../scss/common/list/List.scss'
import { notifyError, notifySuccess } from 'utils'
import { initFacebookSDK } from 'config/facebook'
import { useNavigate, useLocation } from 'react-router'
import { useTableHandlers } from 'hooks/useTableHandlers'
import { IListApiRequest } from 'api/types'
import { Formik, Form, FormikProps, FormikHelpers } from 'formik'
import {
  useDeleteChannelOnboardingMutation,
  useFacebookExchageTokenMutation,
  useGetChannelOnboardingsQuery,
  useCreateAppChannelMutation
} from '../channelOnboardingApiSlice'
import {
  TokenRequest,
  Channel,
  FacebookBusinessResponse,
  AppChannelSaveRequest
} from '../types'
import {
  CHANNEL_FORM_FIELDS,
  AppChannelForm,
  appChannelValidationSchema,
  defaultAppChannelFormValues
} from '../utils'

const ChannelOnboardingDetails: FC = () => {
  const Navigate = useNavigate()
  const location = useLocation()
  const selectedChannel = location?.state?.selectedChannel
  const isAppChannel = selectedChannel === 'AgroBeet-App'
  const [showModal, setShowModal] = useState<boolean>(false)
  const [smsModal, setSmsModal] = useState<boolean>(false)
  const [sdkLoaded, setSdkLoaded] = useState<boolean>(false)
  const [DeleteWhatsAppChannel] = useDeleteChannelOnboardingMutation()
  const [WhatsAppCreateChannel] = useFacebookExchageTokenMutation()
  const [createAppChannel] = useCreateAppChannelMutation()

  const { phoneId, whatsappid, resCode, launchWhatsAppSignup } =
    FbAuthProvider()
  const { NAME, DESCRIPTION, SENDERID, USERACCESSTOKEN, DLTTEMPLATEID } =
    CHANNEL_FORM_FIELDS

  const options = [{ label: 'Channel Details', value: '', onClick: () => {} }]

  const { requestBody, onPageChange, onSortColumn } = useTableHandlers<
    FacebookBusinessResponse,
    IListApiRequest<FacebookBusinessResponse>
  >({
    page: { size: 10, number: 1 },
    filters: []
  })

  const { data, isLoading } = useGetChannelOnboardingsQuery({
    ...requestBody,
    selectedChannel
  })
  const total = data?.totalElements ?? data?.size ?? 0

  useEffect(() => {
    initFacebookSDK()
      .then(() => setSdkLoaded(true))
      .catch(() => setSdkLoaded(false))
  }, [])

  useEffect(() => {
    if (phoneId && whatsappid && resCode) {
      const tokenRequest: TokenRequest = {
        phoneNumberId: phoneId,
        wabaId: whatsappid,
        code: resCode,
        channel: Channel.WHATSAPP
      }
      WhatsAppCreateChannel({ TokenRequestDTO: tokenRequest })
        .then(() => {
          notifySuccess('WhatsApp Account Connected Successfully')
        })
        .catch((error: any) => {
          notifyError(error ?? 'Error creating WhatsApp Channel')
        })
    }
  }, [phoneId, whatsappid, resCode, WhatsAppCreateChannel])

  const handleDeleteChannel = async (rowData: any) => {
    try {
      if (rowData?.id) {
        await DeleteWhatsAppChannel({
          whatsAppChannelId: rowData.id
        }).unwrap()
        notifySuccess('Channel deleted successfully')
      } else {
        notifyError('Channel ID is undefined')
      }
    } catch {
      notifyError('Failed to delete Channel')
    }
  }

  const handleCreateAppChannel = async (
    values: AppChannelForm,
    { setSubmitting }: FormikHelpers<AppChannelForm>
  ) => {
    try {
      const payload: AppChannelSaveRequest = {
        appName: values.name,
        businessInfo: values.businessInfo,
        senderId: values.senderId,
        userAccessToken: values.userAccessToken,
        channel: selectedChannel === 'AgroBeet-App' ? 'INAPP' : selectedChannel,
        dltTemplateId: values.dltTemplateId
      }
      await createAppChannel(payload).unwrap()
      notifySuccess('Channel Created Successfully!')
      setShowModal(false)
      setSmsModal(false)
    } catch (error) {
      notifyError('Failed to create App Channel', { containerId: 'modalToast' })
    } finally {
      setSubmitting(false)
    }
  }
  const renderFormButtons = (formikProps: FormikProps<AppChannelForm>) => (
    <ButtonToolbar className="app-buttons-group">
      <Button
        className="formButton"
        id="reset"
        disabled={formikProps.isValidating || formikProps.isSubmitting}
        onClick={() => {
          formikProps.resetForm()
        }}
      >
        Reset
      </Button>
      <Button
        className="formButton"
        appearance="primary"
        type="submit"
        disabled={formikProps.isValidating || formikProps.isSubmitting}
      >
        Submit
      </Button>
    </ButtonToolbar>
  )

  return (
    <div className="list">
      <Toolbar
        options={options}
        backButton="Back"
        backButtonClick={() => Navigate('/channels')}
        onButtonClick={() => {
          if (selectedChannel === 'AgroBeet-App') {
            setShowModal(true)
          } else if (selectedChannel === 'SMS') {
            setSmsModal(true)
          } else {
            launchWhatsAppSignup()
          }
        }}
        buttonName="Add Channel"
        total={total}
      />
      <div className="list__main-container">
        {total === 0 && !isLoading && (
          <div className="channel-onboarding-card-container">
            <div className="card-header">
              <h3>
                {isAppChannel
                  ? 'No App Channel created yet'
                  : selectedChannel === 'SMS'
                    ? 'No SMS Channel created yet'
                    : 'No WhatsApp account connected yet'}
              </h3>
            </div>
            <p>
              {isAppChannel
                ? 'Create an App channel to connect with users inside AgroBeet.'
                : selectedChannel === 'SMS'
                  ? 'Create an SMS channel to connect with users inside AgroBeet.'
                  : 'Link your WhatsApp Business account to AgroBeet'}
            </p>
            <Button
              appearance="primary"
              onClick={
                isAppChannel
                  ? () => setShowModal(true)
                  : selectedChannel === 'SMS'
                    ? () => setSmsModal(true)
                    : launchWhatsAppSignup
              }
              disabled={!sdkLoaded && !isAppChannel}
            >
              {(() => {
                if (isAppChannel) return 'Create App Channel'
                if (selectedChannel === 'SMS') return 'Create SMS Channel'
                if (sdkLoaded) return 'Login with Facebook'
                return 'Loading...'
              })()}
            </Button>
          </div>
        )}

        {data && total !== 0 && (
          <CardTable
            data={data.content ?? []}
            loading={isLoading}
            onSortColumn={onSortColumn}
            paginated
            pageSizeOptions={[10, 20, 30]}
            total={total}
            defaultPageSize={data?.size ?? 10}
            onPageChange={onPageChange}
            whatsAppInfo
            confirmDelete={handleDeleteChannel}
          />
        )}

        {showModal && (
          <Modal
            open={showModal}
            onClose={() => setShowModal(false)}
            title="Create AgroBeet App Channel"
            size="calc(50% - 120px)"
            secondary
            body={
              <div className="campaign-form-section">
                <Formik
                  initialValues={{
                    ...defaultAppChannelFormValues,
                    channel:
                      selectedChannel === 'AgroBeet-App'
                        ? Channel.INAPP
                        : Channel.SMS
                  }}
                  validationSchema={appChannelValidationSchema}
                  onSubmit={handleCreateAppChannel}
                >
                  {(formikProps: FormikProps<AppChannelForm>) => (
                    <Form className="create-edit">
                      <Panel>
                        <Section title="App Channel Details">
                          <Row>
                            <Col xs={24}>
                              <TextInput
                                id={CHANNEL_FORM_FIELDS.NAME}
                                formik={formikProps}
                                name={NAME}
                                placeholder="Enter app name"
                              />
                              <FormikErrorMessage name={NAME} />
                            </Col>
                          </Row>
                          <Row>
                            <Col xs={24}>
                              <TextInput
                                id={CHANNEL_FORM_FIELDS.DESCRIPTION}
                                formik={formikProps}
                                name={DESCRIPTION}
                                placeholder="Enter business name"
                              />
                              <FormikErrorMessage name={DESCRIPTION} />
                            </Col>
                          </Row>
                        </Section>
                      </Panel>
                      <Panel>{renderFormButtons(formikProps)}</Panel>
                    </Form>
                  )}
                </Formik>
              </div>
            }
          />
        )}
        {smsModal && (
          <Modal
            open={smsModal}
            onClose={() => setSmsModal(false)}
            title="Create Sms Channel"
            size="calc(50% - 120px)"
            secondary
            body={
              <div className="campaign-form-section">
                <Formik
                  initialValues={defaultAppChannelFormValues}
                  validationSchema={appChannelValidationSchema}
                  onSubmit={handleCreateAppChannel}
                >
                  {(formikProps: FormikProps<AppChannelForm>) => (
                    <Form className="create-edit">
                      <Panel>
                        <Section title="Sms Channel Details">
                          <Row>
                            <Col xs={12}>
                              <TextInput
                                id={CHANNEL_FORM_FIELDS.NAME}
                                formik={formikProps}
                                name={NAME}
                                placeholder="Enter channel name"
                              />
                              <FormikErrorMessage name={NAME} />
                            </Col>
                            <Col xs={12}>
                              <TextInput
                                id={CHANNEL_FORM_FIELDS.DESCRIPTION}
                                formik={formikProps}
                                name={DESCRIPTION}
                                placeholder="Enter business name"
                              />
                              <FormikErrorMessage name={DESCRIPTION} />
                            </Col>
                          </Row>
                          <Row>
                            <Col xs={12}>
                              <TextInput
                                id={CHANNEL_FORM_FIELDS.SENDERID}
                                formik={formikProps}
                                name={SENDERID}
                                placeholder="Enter SenderId"
                              />
                              <FormikErrorMessage name={SENDERID} />
                            </Col>
                            <Col xs={12}>
                              <TextInput
                                id={CHANNEL_FORM_FIELDS.DLTTEMPLATEID}
                                formik={formikProps}
                                name={DLTTEMPLATEID}
                                placeholder="Enter Dlt TemplateId"
                              />
                              <FormikErrorMessage name={DLTTEMPLATEID} />
                            </Col>
                          </Row>
                          <Row>
                            <Col xs={12}>
                              <TextInput
                                id={CHANNEL_FORM_FIELDS.USERACCESSTOKEN}
                                formik={formikProps}
                                name={USERACCESSTOKEN}
                                placeholder="Enter Auth key"
                              />
                              <FormikErrorMessage name={USERACCESSTOKEN} />
                            </Col>
                          </Row>
                        </Section>
                      </Panel>
                      <Panel>{renderFormButtons(formikProps)}</Panel>
                    </Form>
                  )}
                </Formik>
              </div>
            }
          />
        )}
      </div>
    </div>
  )
}

export default ChannelOnboardingDetails
